import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { ExternalLink, Smartphone, Apple, Share, Plus, MoreVertical, ArrowLeft, Download, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InstallApp() {
  const { slug } = useParams();
  const { t, isRTL } = useI18n();
  const [app, setApp] = useState(null);
  const [error, setError] = useState("");
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  // Capture the browser's install prompt event for one-tap install on Android/Chrome/Edge
  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const onOneTapInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice && choice.outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPwaLaunch = params.get("pwa") === "1";
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const launchedAsApp = isPwaLaunch || isStandalone;

    api.get(`/public/apps/${slug}`).then((r) => {
      setApp(r.data);

      const existing = document.getElementById("dynamic-manifest");
      if (existing) existing.remove();
      const link = document.createElement("link");
      link.id = "dynamic-manifest";
      link.rel = "manifest";
      link.href = `${BACKEND_URL}/api/public/apps/${slug}/manifest.json`;
      document.head.appendChild(link);

      const themeId = "dynamic-theme-color";
      let themeMeta = document.getElementById(themeId);
      if (!themeMeta) {
        themeMeta = document.createElement("meta");
        themeMeta.id = themeId;
        themeMeta.name = "theme-color";
        document.head.appendChild(themeMeta);
      }
      themeMeta.content = r.data.theme_color || "#0A0A0A";

      const appleId = "dynamic-apple-icon";
      let appleIcon = document.getElementById(appleId);
      if (!appleIcon) {
        appleIcon = document.createElement("link");
        appleIcon.id = appleId;
        appleIcon.rel = "apple-touch-icon";
        document.head.appendChild(appleIcon);
      }
      const fallbackIcon = `https://www.google.com/s2/favicons?domain=${(()=>{try{return new URL(r.data.url).hostname}catch{return ""}})()}&sz=256`;
      appleIcon.href = r.data.icon_url || fallbackIcon;

      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
      }

      if (launchedAsApp && r.data.url) {
        window.location.replace(r.data.url);
      }
    }).catch((e) => setError(e.response?.data?.detail || "App not found"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="surface-card p-10 text-center max-w-md">
          <h1 className="font-display text-3xl font-bold tracking-tight">404</h1>
          <p className="text-zinc-500 mt-2">{error}</p>
          <Link to="/" className="btn btn-secondary mt-6 inline-flex">
            <ArrowLeft className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} strokeWidth={2} />
            Home
          </Link>
        </div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="surface-card px-5 py-3 text-sm text-zinc-500">{t("common_loading")}</div>
      </div>
    );
  }

  const initial = (app.short_name || app.name || "A").charAt(0).toUpperCase();
  const installUrl = window.location.href.split("?")[0];

  return (
    <div className="relative min-h-screen">
      <div className="aurora opacity-60" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 z-10">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* App identity */}
          <div className="surface-card p-8 sm:p-10 text-center">
            <div
              className="w-24 h-24 mx-auto rounded-3xl grid place-items-center overflow-hidden mb-5"
              style={{
                background: app.theme_color || "#0A0A0A",
                boxShadow: "0 18px 40px -16px rgba(15, 23, 42, 0.35), inset 0 0 0 1px rgba(255,255,255,0.06)",
              }}
              data-testid="install-app-icon"
            >
              {app.icon_url ? (
                <img src={app.icon_url} alt={app.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <span className="font-display font-bold text-4xl text-white">{initial}</span>
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="install-app-name">{app.name}</h1>
            {app.description && <p className="mt-3 text-zinc-600 max-w-sm mx-auto">{app.description}</p>}

            {/* One-tap install (Android/Chrome) — appears only when browser allows it */}
            {installed ? (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold" data-testid="install-installed-badge">
                <Check className="w-4 h-4" strokeWidth={2.5} />
                {t("install_installed")}
              </div>
            ) : deferredPrompt ? (
              <button
                type="button"
                onClick={onOneTapInstall}
                data-testid="install-one-tap"
                className="btn btn-primary btn-lg mt-6 inline-flex"
              >
                <Download className="w-4 h-4" strokeWidth={2} />
                {t("install_one_tap")}
              </button>
            ) : null}

            <div className="surface-card mt-7 p-4 inline-block bg-white">
              <QRCodeSVG value={installUrl} size={172} bgColor="#FFFFFF" fgColor="#0A0A0A" includeMargin={false} />
            </div>
            <p className="mt-4 text-xs font-medium text-zinc-500 uppercase tracking-[0.16em]">{t("install_sub")}</p>

            <a
              href={app.url}
              target="_blank"
              rel="noreferrer"
              data-testid="install-open-website"
              className="btn btn-primary btn-lg mt-7 inline-flex"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={2} />
              {t("install_open")}
            </a>
          </div>

          {/* Instructions */}
          <div className="space-y-5">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
              <Smartphone className="w-7 h-7" strokeWidth={1.6} />
              {t("install_title")}
            </h2>

            <div className="surface-card p-7" data-testid="install-ios-block">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2 mb-4">
                <Apple className="w-5 h-5" strokeWidth={1.6} />
                {t("install_ios")}
              </h3>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">1</span>
                  <span className="flex items-center gap-1.5 text-zinc-700">{t("install_ios_1")} <Share className="w-3.5 h-3.5" strokeWidth={1.8} /></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">2</span>
                  <span className="flex items-center gap-1.5 text-zinc-700">{t("install_ios_2")} <Plus className="w-3.5 h-3.5" strokeWidth={1.8} /></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">3</span>
                  <span className="text-zinc-700">{t("install_ios_3")}</span>
                </li>
              </ol>
            </div>

            <div className="surface-card p-7" data-testid="install-android-block">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2 mb-4">
                <Smartphone className="w-5 h-5" strokeWidth={1.6} />
                {t("install_android")}
              </h3>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">1</span>
                  <span className="flex items-center gap-1.5 text-zinc-700">{t("install_android_1")} <MoreVertical className="w-3.5 h-3.5" strokeWidth={1.8} /></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">2</span>
                  <span className="text-zinc-700">{t("install_android_2")}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-zinc-100 grid place-items-center font-medium text-xs flex-shrink-0">3</span>
                  <span className="text-zinc-700">{t("install_android_3")}</span>
                </li>
              </ol>
            </div>

            <p className="text-xs font-medium text-zinc-400 uppercase tracking-[0.16em] text-center pt-2">
              {t("install_made_with")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
